import axios, { AxiosInstance } from "axios";
import { LICENSE_API_KEY } from "../env";
import { getApiBaseUrl, getFeatureDeployHeaders } from "./utils";

/** `auth_method` value that routes session refresh at the backend. */
export const BACKEND_AUTH_METHOD = "backend";

/**
 * Whether the session in this browser was established through the backend.
 * Operations on an existing session key off this rather than the feature flag:
 * the flag is a rollout switch that can be turned off under a session that is
 * already running, and such a session still has to be refreshed the way it was
 * created. Only the decision to start a *new* login reads the flag.
 */
export const isBackendSession = () =>
  localStorage.getItem("auth_method") === BACKEND_AUTH_METHOD;

/** The Cognito challenges the backend login endpoint can hand back. */
export const CHALLENGE = {
  SOFTWARE_TOKEN_MFA: "SOFTWARE_TOKEN_MFA",
  NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED",
} as const;

/**
 * One response shape for both legs of the flow: tokens when authentication
 * completed, or a challenge plus the session token that answers it. The
 * refresh token is never here — the backend keeps it in an HttpOnly cookie.
 */
export type AuthResponse = {
  accessToken?: string;
  idToken?: string;
  challengeName?: string;
  session?: string;
};

/**
 * Auth calls deliberately bypass `apiClient`: its response interceptor reads
 * every 401 as an expired session and answers with a storage wipe and a
 * redirect to /login, which would swallow the 401 a mistyped password is
 * supposed to produce. `withCredentials` carries the refresh cookie.
 *
 * Built on first use, not at module load, since the base URL is derived from
 * `window.location`.
 */
let client: AxiosInstance | undefined;

const authClient = () => {
  if (!client) {
    client = axios.create({
      baseURL: getApiBaseUrl(),
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        // These routes carry no bearer token, so the gateway key is what gets
        // them through — same as the pre-login endpoints.
        "x-api-key": LICENSE_API_KEY,
        ...getFeatureDeployHeaders(),
      },
    });
  }
  return client;
};

const post = async (path: string, body?: unknown): Promise<AuthResponse> => {
  const response = await authClient().post(`/user-management/auth/${path}`, body);
  return response.data ?? {};
};

// The 401 interceptor and the background session refresh can both call refresh
// at once (sleep/wake fires the timer and the first request 401s together).
// The backend rotates the refresh cookie, so a second in-flight POST presents
// a cookie that was just consumed and fails. Sharing one request means every
// waiter gets the same ID token, and the caller that publishes the session
// still does.
let refreshInFlight: Promise<AuthResponse> | undefined;

// Set while logout's revoke is in flight so no new refresh mints against the
// cookie being revoked. A login that comes back with tokens reopens it.
let sessionRevoked = false;

export class SessionRevokedError extends Error {
  constructor() {
    super("Session was revoked by logout");
    this.name = "SessionRevokedError";
  }
}

/** Test-only: clears the module latch between tests. */
export const __resetAuthStateForTests = () => {
  refreshInFlight = undefined;
  sessionRevoked = false;
};

export const authApi = {
  login: async (username: string, password: string) => {
    const response = await post("login", { username, password });
    if (response.idToken) {
      sessionRevoked = false;
    }
    return response;
  },

  respondToChallenge: async (
    challengeName: string,
    session: string,
    challengeResponses: Record<string, string>
  ) => {
    const response = await post("login", { challengeName, session, challengeResponses });
    if (response.idToken) {
      sessionRevoked = false;
    }
    return response;
  },

  /** Mints a fresh ID token from the refresh cookie. */
  refresh: () => {
    if (sessionRevoked) {
      return Promise.reject(new SessionRevokedError());
    }
    if (!refreshInFlight) {
      refreshInFlight = post("refresh").finally(() => {
        refreshInFlight = undefined;
      });
    }
    return refreshInFlight;
  },

  /**
   * Revokes the refresh token server-side and expires the cookie. Idempotent:
   * a missing or already-revoked cookie still answers 200, since "no session"
   * is the intended end state either way.
   */
  logout: async (): Promise<{ message: string }> => {
    sessionRevoked = true;
    // Waits on in-flight refresh: each refresh rotates the cookie.
    await refreshInFlight?.catch(() => {});
    try {
      return (await post("logout")) as unknown as { message: string };
    } catch (error) {
      sessionRevoked = false;
      throw error;
    }
  },
};

// JWT segments are base64url, where `-` and `_` stand in for `+` and `/`, both
// of which atob rejects.
const decodePayload = (jwt: string) =>
  JSON.parse(atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));

/**
 * Rebuilds the slice of `CognitoUserSession` the app actually reads —
 * `idToken.jwtToken` and `idToken.payload` — from a backend-issued ID token, so
 * Main's login gate, `useSessionRefreshTimer` and `user-context` keep working
 * unchanged. There is no `refreshToken` field: it lives in the cookie, and
 * every consumer already reads that one optionally.
 */
export const toUserSession = (idToken: string) => ({
  idToken: { jwtToken: idToken, payload: decodePayload(idToken) },
});

/**
 * The backend collapses every rejected credential — wrong password, bad TOTP
 * code, a new password that fails policy, an expired challenge session — into
 * one flat 401, so only the caller knows what a rejection means where it sits.
 */
export const authErrorMessage = (error: any, rejected = "Invalid email or password") =>
  error?.response?.status === 401 ? rejected : "Something went wrong";
